/**
 * useCif — loads CustInq for a CIF id + exposes loading/error/reload.
 *
 * Runs the raw Function response through `parseCustInq` (ported from legacy
 * `JxChangeService.custInq` line-for-line) so consumers get a flat
 * `ParsedCustInq` with `firstName`, `taxId`, `addr`, etc. — instead of the
 * nested jXRL shape `CustRec.CustDetail.PersonName.FirstName`.
 *
 * Prior bug: this hook returned `anyRes.Customer ?? anyRes.data ?? res` cast
 * to `CustInqResult`, which left components to dig into
 * `CustInqRs_MType.CustRec_CType` — a wrapper that never existed in jXRL.
 * Every KVPair on the CIF detail page rendered "—". See
 * `legacy/JxChangeService.ts:170-303` for the canonical parser.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApi } from './useApi';
import { parseCustInq, type ParsedCustInq } from '@/utils/custInqParser';

export interface UseCifState {
  /** Parsed flat shape — null while loading / on error. */
  data: ParsedCustInq | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useCif(cifId: string | undefined): UseCifState {
  const { client, makeSignal } = useApi();
  const [data, setData] = useState<ParsedCustInq | null>(null);
  const [loading, setLoading] = useState<boolean>(!!cifId);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);

  useEffect(() => {
    if (!cifId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const signal = makeSignal();
    // includeUdf: the RCM CIF surface shows the 2nd ID block, which lives in
    // PCB's custom 800-series UDFs and costs a second x_BusDetail call.
    client.accounts.custInq(cifId, { signal }, true).then(
      (res): void => {
        const parsed = parseCustInq(res, cifId);
        if (!parsed.success) {
          setError(parsed.error || 'Not found');
          setData(null);
        } else {
          setData(parsed);
        }
        setLoading(false);
      },
      (err: unknown): void => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cifId, tick]);

  const reload = useCallback((): void => setTick((t) => t + 1), []);
  return { data, loading, error, reload };
}
